import React, { useEffect, useRef, useState } from "react";
import { api, formatCHF, formatNum, currentMonthString, formatMonat, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Upload, Sparkles, AlertTriangle, CheckCircle2, MinusCircle, PlusCircle } from "lucide-react";
import { toast } from "sonner";

function hoursToHHMM(h) {
  if (h == null || isNaN(h)) return "—";
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  return `${hours}:${String(mins).padStart(2, "0")}`;
}

const DIFF_LABELS = {
  zeit_stunden: "Zeit",
  km: "KM",
  verladung_15: "V15",
  verladung_30: "V30",
  entladung_10: "E10",
};

export default function TagestourenAbgleich() {
  const [monat, setMonat] = useState(currentMonthString());
  const [snapshots, setSnapshots] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeSnapshotId, setActiveSnapshotId] = useState(null);
  const fileRef = useRef();
  const [filter, setFilter] = useState("all"); // all | diffs | missing_in_report | missing_in_aufwand

  const loadSnapshots = async () => {
    const r = await api.get("/tagestouren-abgleich/snapshots", { params: { monat } });
    setSnapshots(r.data);
    if (r.data.length > 0 && !activeSnapshotId) {
      await loadSnapshot(r.data[0].id);
    } else if (r.data.length === 0) {
      setActiveSnapshotId(null);
      setComparison([]);
    }
  };

  const loadSnapshot = async (sid) => {
    const r = await api.get(`/tagestouren-abgleich/snapshot/${sid}`);
    setActiveSnapshotId(sid);
    setComparison(r.data.comparison || []);
  };

  useEffect(() => {
    loadSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monat]);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/tagestouren-abgleich/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.error) {
        toast.error("Parser-Fehler: " + r.data.error);
      } else {
        toast.success(`Abgleich geladen: ${r.data.rows_count} Touren erkannt.`);
        setActiveSnapshotId(r.data.snapshot_id);
        setComparison(r.data.comparison || []);
        if (r.data.monat && r.data.monat !== monat) setMonat(r.data.monat);
        await loadSnapshots();
      }
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applyToReport = async (row, fields) => {
    try {
      if (row.report?.id) {
        await api.post("/tagestouren-abgleich/apply", { report_id: row.report.id, fields });
      } else if (row.aufwand) {
        await api.post("/tagestouren-abgleich/apply", {
          create: true,
          datum: row.aufwand.datum,
          tournummer: row.aufwand.tournummer,
          fields,
        });
      }
      toast.success("Übernommen");
      if (activeSnapshotId) await loadSnapshot(activeSnapshotId);
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  const applyAll = async (row) => {
    if (!row.aufwand) return;
    const fields = {
      arbeitszeit_stunden: row.aufwand.zeit_stunden,
      km: row.aufwand.km,
      verladung_15: row.aufwand.verladung_15,
      verladung_30: row.aufwand.verladung_30,
      entladung_10: row.aufwand.entladung_10,
    };
    await applyToReport(row, fields);
  };

  const filtered = comparison.filter((c) => {
    if (filter === "all") return true;
    if (filter === "diffs") return c.match_status === "matched" && (c.diffs?.length || 0) > 0;
    if (filter === "missing_in_report") return c.match_status === "missing_in_report";
    if (filter === "missing_in_aufwand") return c.match_status === "missing_in_aufwand";
    return true;
  });

  const counts = {
    matched: comparison.filter((c) => c.match_status === "matched").length,
    withDiffs: comparison.filter((c) => c.match_status === "matched" && (c.diffs?.length || 0) > 0).length,
    missingInReport: comparison.filter((c) => c.match_status === "missing_in_report").length,
    missingInAufwand: comparison.filter((c) => c.match_status === "missing_in_aufwand").length,
  };

  return (
    <PageContainer>
      <PageHeader
        title="Tagestouren-Abgleich"
        subtitle="7Days Aufwand TT hochladen und Tour-für-Tour gegen unsere Daten prüfen"
        testId="tt-abgleich-page"
      >
        <MonthPicker value={monat} onChange={setMonat} testId="tt-abgleich-month" />
        <input
          type="file"
          ref={fileRef}
          className="hidden"
          accept="application/pdf,image/*"
          onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
          data-testid="tt-abgleich-input"
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bg-[#0055FF] hover:bg-[#0040CC]"
          data-testid="tt-abgleich-upload"
        >
          {uploading ? (
            <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          {uploading ? "OCR läuft…" : "Aufwand TT hochladen"}
        </Button>
      </PageHeader>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="border-neutral-200 bg-white p-3" data-testid="tt-card-matched">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-[#008A00]" /> Übereinstimmend
          </div>
          <div className="text-2xl font-semibold nivara-num mt-1">{counts.matched - counts.withDiffs}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3" data-testid="tt-card-diffs">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500">
            <AlertTriangle className="h-3.5 w-3.5 text-[#FFB800]" /> Mit Differenzen
          </div>
          <div className="text-2xl font-semibold nivara-num mt-1">{counts.withDiffs}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3" data-testid="tt-card-missing-report">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500">
            <PlusCircle className="h-3.5 w-3.5 text-red-600" /> Fehlt bei uns
          </div>
          <div className="text-2xl font-semibold nivara-num mt-1">{counts.missingInReport}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3" data-testid="tt-card-missing-aufwand">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500">
            <MinusCircle className="h-3.5 w-3.5 text-neutral-500" /> Fehlt bei 7Days
          </div>
          <div className="text-2xl font-semibold nivara-num mt-1">{counts.missingInAufwand}</div>
        </Card>
      </div>

      {/* Snapshots history */}
      {snapshots.length > 0 && (
        <Card className="border-neutral-200 bg-white p-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-neutral-500 mr-2">Hochgeladene Snapshots:</span>
            {snapshots.map((s) => (
              <Button
                key={s.id}
                variant={s.id === activeSnapshotId ? "default" : "outline"}
                size="sm"
                onClick={() => loadSnapshot(s.id)}
                className={s.id === activeSnapshotId ? "bg-[#0055FF] hover:bg-[#0040CC]" : ""}
                data-testid={`tt-snapshot-${s.id}`}
              >
                {formatMonat(s.monat)} · {s.rows_count} Touren · {new Date(s.created_at).toLocaleString("de-CH")}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[
          { k: "all", label: `Alle (${comparison.length})` },
          { k: "diffs", label: `Differenzen (${counts.withDiffs})` },
          { k: "missing_in_report", label: `Fehlt bei uns (${counts.missingInReport})` },
          { k: "missing_in_aufwand", label: `Fehlt bei 7Days (${counts.missingInAufwand})` },
        ].map((f) => (
          <Button
            key={f.k}
            variant={filter === f.k ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.k)}
            className={filter === f.k ? "bg-[#0055FF] hover:bg-[#0040CC]" : ""}
            data-testid={`tt-filter-${f.k}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Comparison table */}
      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="tt-comparison-table">
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Tour</TableHead>
                <TableHead className="text-right">Zeit unser</TableHead>
                <TableHead className="text-right">Zeit 7Days</TableHead>
                <TableHead className="text-right">KM unser</TableHead>
                <TableHead className="text-right">KM 7Days</TableHead>
                <TableHead className="text-center">V15</TableHead>
                <TableHead className="text-center">V30</TableHead>
                <TableHead className="text-center">E10</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-center text-neutral-500 py-10">
                    {comparison.length === 0
                      ? "Noch kein Abgleich – lade ein 7Days Aufwand TT PDF hoch."
                      : "Keine Zeilen für den gewählten Filter."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((c) => {
                const a = c.aufwand;
                const r = c.report;
                const diffField = (f) => c.diffs?.some((d) => d.field === f);
                const cellCls = (f) => (diffField(f) ? "text-amber-700 font-semibold" : "");
                const status = c.match_status;
                return (
                  <TableRow key={c.key} className={status === "matched" && c.diffs.length === 0 ? "bg-green-50/30" : status !== "matched" ? "bg-red-50/30" : "bg-amber-50/40"}>
                    <TableCell className="font-mono text-xs">{formatDatum(c.datum) || "—"}</TableCell>
                    <TableCell className="font-medium">{c.tournummer || "—"}</TableCell>
                    <TableCell className={`text-right nivara-num text-xs ${cellCls("zeit_stunden")}`}>
                      {r ? `${formatNum(r.arbeitszeit_stunden, 2)} (${hoursToHHMM(r.arbeitszeit_stunden)})` : "—"}
                    </TableCell>
                    <TableCell className={`text-right nivara-num text-xs ${cellCls("zeit_stunden")}`}>
                      {a ? `${formatNum(a.zeit_stunden, 2)} (${hoursToHHMM(a.zeit_stunden)})` : "—"}
                    </TableCell>
                    <TableCell className={`text-right nivara-num ${cellCls("km")}`}>{r ? formatNum(r.km, 0) : "—"}</TableCell>
                    <TableCell className={`text-right nivara-num ${cellCls("km")}`}>{a ? formatNum(a.km, 0) : "—"}</TableCell>
                    <TableCell className={`text-center nivara-num ${cellCls("verladung_15")}`}>
                      {r?.verladung_15 ?? "—"} / {a?.verladung_15 ?? "—"}
                    </TableCell>
                    <TableCell className={`text-center nivara-num ${cellCls("verladung_30")}`}>
                      {r?.verladung_30 ?? "—"} / {a?.verladung_30 ?? "—"}
                    </TableCell>
                    <TableCell className={`text-center nivara-num ${cellCls("entladung_10")}`}>
                      {r?.entladung_10 ?? "—"} / {a?.entladung_10 ?? "—"}
                    </TableCell>
                    <TableCell>
                      {status === "matched" && c.diffs.length === 0 && <Badge className="bg-[#008A00] text-white">OK</Badge>}
                      {status === "matched" && c.diffs.length > 0 && (
                        <Badge className="bg-[#FFB800] text-neutral-900" title={c.diffs.map((d) => `${DIFF_LABELS[d.field]||d.field}: ${d.delta>0?"+":""}${d.delta}`).join(", ")}>
                          {c.diffs.length} Δ
                        </Badge>
                      )}
                      {status === "missing_in_report" && <Badge className="bg-red-600 text-white">Fehlt bei uns</Badge>}
                      {status === "missing_in_aufwand" && <Badge variant="outline">Fehlt bei 7Days</Badge>}
                    </TableCell>
                    <TableCell>
                      {a && (status === "missing_in_report" || c.diffs.length > 0) && (
                        <Button size="sm" variant="outline" onClick={() => applyAll(c)} data-testid={`tt-apply-${c.key}`}>
                          {status === "missing_in_report" ? "Anlegen" : "Übernehmen"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="text-xs text-neutral-500 mt-3">
        <span className="font-semibold">Legende:</span> V15/V30/E10 = unser / 7Days. Δ markiert Felder mit Differenz (Toleranz: Zeit ±0.05 h, KM ±1 km).
        Wartezeiten bei 7Days werden aktuell als „Zeit“-Spalte mitgezählt — bitte separat im Tour-Detail prüfen.
      </div>
    </PageContainer>
  );
}
